<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  insight: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click', 'markRead', 'dismiss', 'action']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const isUnread = computed(() => props.insight.status === 'unread');

const priorityConfig = computed(() => {
  const map = {
    high: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.PRIORITY.HIGH'),
      classes: 'bg-r-50 text-r-800 dark:bg-r-900 dark:text-r-200',
    },
    medium: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.PRIORITY.MEDIUM'),
      classes: 'bg-y-50 text-y-800 dark:bg-y-900 dark:text-y-200',
    },
    low: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.PRIORITY.LOW'),
      classes: 'bg-g-50 text-g-800 dark:bg-g-900 dark:text-g-200',
    },
  };
  return map[props.insight.priority] || map.medium;
});

const typeConfig = computed(() => {
  const map = {
    daily_briefing: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.TYPES.DAILY_BRIEFING'),
      icon: 'i-lucide-calendar',
    },
    alert: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.TYPES.ALERT'),
      icon: 'i-lucide-alert-triangle',
    },
    suggestion: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.TYPES.SUGGESTION'),
      icon: 'i-lucide-lightbulb',
    },
    conversation_summary: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.TYPES.CONVERSATION_SUMMARY'),
      icon: 'i-lucide-message-circle',
    },
  };
  return map[props.insight.insight_type] || map.daily_briefing;
});

// Entity link from insight.data
const entityInfo = computed(() => {
  const data = props.insight.data || {};
  const accountId = route.params.accountId;
  if (data.deal_id) {
    return {
      icon: 'i-lucide-handshake',
      label: data.deal_title || `Deal #${data.deal_id}`,
      path: `/app/accounts/${accountId}/crm/deals/${data.deal_id}`,
    };
  }
  if (data.conversation_id) {
    return {
      icon: 'i-lucide-message-circle',
      label: `${t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.CONVERSATION')} #${data.conversation_display_id || data.conversation_id}`,
      path: `/app/accounts/${accountId}/conversations/${data.conversation_id}`,
    };
  }
  if (data.contact_id) {
    return {
      icon: 'i-lucide-user',
      label: data.contact_name || `Contact #${data.contact_id}`,
      path: `/app/accounts/${accountId}/contacts/${data.contact_id}`,
    };
  }
  return null;
});

const navigateToEntity = () => {
  if (entityInfo.value?.path) {
    router.push(entityInfo.value.path);
  }
};

// Conversation summary specific
const isConversationSummary = computed(
  () => props.insight.insight_type === 'conversation_summary'
);

// Aggregated stale conversations summary
const isStaleSummary = computed(
  () => props.insight.data?.subtype === 'stale_conversations_summary'
);

const staleBreakdown = computed(() => {
  const data = props.insight.data || {};
  return data.breakdown || [];
});

const stalePendingPath = computed(() => {
  const accountId = route.params.accountId;
  return `/app/accounts/${accountId}/conversations?status=open&sort=last_activity_at_asc`;
});

const sentiment = computed(() => {
  const data = props.insight.data || {};
  if (!data.sentiment) return null;
  const map = {
    positive: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.SENTIMENT.POSITIVE'),
      icon: 'i-lucide-smile',
      classes: 'text-g-600',
    },
    neutral: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.SENTIMENT.NEUTRAL'),
      icon: 'i-lucide-meh',
      classes: 'text-y-600',
    },
    negative: {
      label: t('CAPTAIN.BRAIN.INSIGHTS.SENTIMENT.NEGATIVE'),
      icon: 'i-lucide-frown',
      classes: 'text-r-600',
    },
  };
  return map[data.sentiment] || null;
});

const actionItems = computed(() => {
  const data = props.insight.data || {};
  return data.action_items || [];
});

const actionIcon = type => {
  const map = {
    create_task: 'i-lucide-check-square',
    schedule_message: 'i-lucide-clock',
    add_note: 'i-lucide-sticky-note',
  };
  return map[type] || 'i-lucide-zap';
};

const itemPriorityClasses = priority => {
  const map = {
    high: 'bg-r-50 text-r-800 dark:bg-r-900 dark:text-r-200',
    medium: 'bg-y-50 text-y-800 dark:bg-y-900 dark:text-y-200',
    low: 'bg-g-50 text-g-800 dark:bg-g-900 dark:text-g-200',
  };
  return map[priority] || map.medium;
};

const handleAction = action => {
  emit('action', {
    insightId: props.insight.id,
    conversationId: props.insight.data?.conversation_id,
    contactId: props.insight.data?.contact_id,
    ...action,
  });
};

// Relative time for compact mode
const relativeTime = computed(() => {
  const created = props.insight.created_at;
  if (!created) return '';
  const date =
    typeof created === 'number' ? new Date(created * 1000) : new Date(created);
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.JUST_NOW');
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
});

// Full date for full mode
const formattedDate = computed(() => {
  const created = props.insight.created_at;
  if (!created) return '';
  const date =
    typeof created === 'number' ? new Date(created * 1000) : new Date(created);
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
});
</script>

<template>
  <!-- Compact mode for widget -->
  <div
    v-if="compact"
    class="flex items-start gap-3 p-3 rounded-lg hover:bg-n-alpha-black2 cursor-pointer transition-colors"
    @click="emit('click', insight)"
  >
    <Icon
      :icon="typeConfig.icon"
      class="size-4 mt-0.5 shrink-0"
      :class="{
        'text-r-600': insight.priority === 'high',
        'text-y-600': insight.priority === 'medium',
        'text-g-600': insight.priority === 'low',
      }"
    />
    <div class="flex-1 min-w-0">
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12 truncate">
          {{ insight.title }}
        </span>
        <span class="text-xs text-n-slate-10 whitespace-nowrap">
          {{ relativeTime }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11 mt-0.5 mb-0 line-clamp-1">
        {{ insight.content }}
      </p>
      <div
        v-if="entityInfo || isUnread"
        class="flex items-center gap-3 mt-1.5"
      >
        <button
          v-if="entityInfo"
          class="inline-flex items-center gap-1 text-xs text-b-600 hover:text-b-700 font-medium"
          @click.stop="navigateToEntity"
        >
          <Icon :icon="entityInfo.icon" class="size-3" />
          {{ entityInfo.label }}
        </button>
        <button
          v-if="isUnread"
          class="inline-flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 ml-auto"
          :title="t('CAPTAIN.BRAIN.INSIGHTS.MARK_READ')"
          @click.stop="emit('markRead', insight.id)"
        >
          <Icon icon="i-lucide-check" class="size-3" />
        </button>
      </div>
    </div>
  </div>

  <!-- Full mode for insights page -->
  <div
    v-else
    class="flex flex-col gap-3 p-5 rounded-xl border bg-n-background transition-all duration-200 cursor-pointer"
    :class="[
      isUnread
        ? 'border-l-4 border-l-b-600 border-t-n-weak border-r-n-weak border-b-n-weak hover:shadow-sm'
        : 'border-n-weak hover:border-n-slate-6',
    ]"
    @click="emit('click', insight)"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex items-center gap-2 flex-wrap">
        <span
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
          :class="priorityConfig.classes"
        >
          {{ priorityConfig.label }}
        </span>
        <span
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-n-alpha-black2 text-n-slate-11"
        >
          <Icon :icon="typeConfig.icon" class="size-3" />
          {{ typeConfig.label }}
        </span>
      </div>
      <span class="text-xs text-n-slate-10 whitespace-nowrap">
        {{ formattedDate }}
      </span>
    </div>

    <div class="flex flex-col gap-1">
      <h3
        class="text-sm font-medium truncate"
        :class="isUnread ? 'text-n-slate-12' : 'text-n-slate-11'"
      >
        {{ insight.title }}
      </h3>
      <p class="text-xs text-n-slate-11 mb-0 line-clamp-3">
        {{ insight.content }}
      </p>
    </div>

    <!-- Aggregated stale conversations summary -->
    <div v-if="isStaleSummary" class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <span class="text-3xl font-bold text-n-slate-12">
          {{ insight.data.total }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.BRAIN.INSIGHTS.STALE_SUMMARY.CONVERSATIONS') }}
        </span>
      </div>

      <div
        v-if="staleBreakdown.length > 0"
        class="flex flex-col gap-1"
      >
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAPTAIN.BRAIN.INSIGHTS.STALE_SUMMARY.BY_ASSIGNEE') }}
        </span>
        <div class="flex flex-col gap-1">
          <div
            v-for="item in staleBreakdown"
            :key="item.assignee_id || 'unassigned'"
            class="flex items-center justify-between py-1 px-2 rounded bg-n-alpha-black2"
          >
            <span class="text-xs text-n-slate-12">
              {{ item.assignee_name }}
            </span>
            <span class="text-xs font-medium text-n-slate-11">
              {{ item.count }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2 text-xs text-n-slate-10">
        <Icon icon="i-lucide-clock" class="size-3" />
        {{ t('CAPTAIN.BRAIN.INSIGHTS.STALE_SUMMARY.OLDEST', { hours: insight.data.oldest_hours }) }}
      </div>

      <router-link
        :to="stalePendingPath"
        class="inline-flex items-center gap-1.5 text-xs text-b-600 hover:text-b-700 font-medium"
        @click.stop
      >
        <Icon icon="i-lucide-external-link" class="size-3.5" />
        {{ t('CAPTAIN.BRAIN.INSIGHTS.STALE_SUMMARY.VIEW_CONVERSATIONS') }}
      </router-link>
    </div>

    <!-- Sentiment badge (conversation_summary only) -->
    <div
      v-if="isConversationSummary && sentiment"
      class="flex items-center gap-2"
    >
      <span
        class="inline-flex items-center gap-1 text-xs font-medium"
        :class="sentiment.classes"
      >
        <Icon :icon="sentiment.icon" class="size-3.5" />
        {{ sentiment.label }}
      </span>
    </div>

    <!-- Entity link -->
    <div v-if="entityInfo && !isStaleSummary" class="flex items-center">
      <button
        class="inline-flex items-center gap-1.5 text-xs text-b-600 hover:text-b-700 font-medium"
        @click.stop="navigateToEntity"
      >
        <Icon :icon="entityInfo.icon" class="size-3.5" />
        {{ entityInfo.label }}
      </button>
    </div>

    <!-- Action items (conversation_summary only) -->
    <div
      v-if="isConversationSummary && actionItems.length > 0"
      class="flex flex-col gap-2 pt-2 border-t border-n-weak"
    >
      <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
        {{ t('CAPTAIN.BRAIN.INSIGHTS.ACTION_ITEMS') }}
      </span>
      <div
        v-for="(item, idx) in actionItems"
        :key="idx"
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-black2"
      >
        <div class="flex items-start gap-2">
          <span
            class="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 mt-0.5"
            :class="itemPriorityClasses(item.priority)"
          >
            {{ item.priority }}
          </span>
          <span class="text-sm text-n-slate-12">{{ item.text }}</span>
        </div>
        <div
          v-if="item.actions && item.actions.length > 0"
          class="flex items-center gap-2 ml-8"
        >
          <button
            v-for="action in item.actions"
            :key="action.type"
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-black2 transition-colors"
            @click.stop="handleAction(action)"
          >
            <Icon :icon="actionIcon(action.type)" class="size-3" />
            {{ action.label }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isUnread"
      class="flex items-center gap-2 mt-auto pt-2 border-t border-n-weak"
    >
      <Button
        :label="t('CAPTAIN.BRAIN.INSIGHTS.MARK_READ')"
        icon="i-lucide-check"
        size="xs"
        variant="ghost"
        @click.stop="emit('markRead', insight.id)"
      />
      <Button
        :label="t('CAPTAIN.BRAIN.INSIGHTS.DISMISS')"
        icon="i-lucide-x"
        size="xs"
        variant="ghost"
        @click.stop="emit('dismiss', insight.id)"
      />
    </div>
  </div>
</template>
